import { useState, useRef, useEffect } from "react";
import { ArrowRight, Share2, Check, Copy, X } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

const INDUSTRIES = [
  { id: "ecommerce", tKey: "ind_ecommerce", avgOrder: 85, convRate: 2.5, aiTraffic: 3200 },
  { id: "saas", tKey: "ind_saas", avgOrder: 120, convRate: 1.8, aiTraffic: 2800 },
  { id: "construction", tKey: "ind_construction", avgOrder: 2500, convRate: 0.5, aiTraffic: 1400 },
  { id: "realestate", tKey: "ind_realestate", avgOrder: 5000, convRate: 0.3, aiTraffic: 1800 },
  { id: "healthcare", tKey: "ind_healthcare", avgOrder: 200, convRate: 3.0, aiTraffic: 2600 },
  { id: "restaurant", tKey: "ind_restaurant", avgOrder: 35, convRate: 4.0, aiTraffic: 3500 },
  { id: "legal", tKey: "ind_legal", avgOrder: 800, convRate: 0.8, aiTraffic: 1200 },
  { id: "education", tKey: "ind_education", avgOrder: 150, convRate: 2.0, aiTraffic: 2400 },
  { id: "marketing", tKey: "ind_marketing", avgOrder: 500, convRate: 1.2, aiTraffic: 2000 },
  { id: "travel", tKey: "ind_travel", avgOrder: 300, convRate: 1.5, aiTraffic: 3000 },
  { id: "fitness", tKey: "ind_fitness", avgOrder: 60, convRate: 3.5, aiTraffic: 2200 },
  { id: "auto", tKey: "ind_auto", avgOrder: 400, convRate: 0.8, aiTraffic: 1600 },
  { id: "other", tKey: "ind_other", avgOrder: 100, convRate: 2.0, aiTraffic: 2000 },
];

const SITE_URL = "https://toprankpulse.com";

function ShareDropdown({ shareText, t, onClose }) {
  const [copied, setCopied] = useState(false);
  const [igCopied, setIgCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const fullText = `${shareText} ${t.share_find_out || "Find out how much you're losing"} ${SITE_URL}`;

  const shareX = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(fullText)}`, "_blank", "noopener");
  };
  const shareFB = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SITE_URL)}&quote=${encodeURIComponent(shareText)}`, "_blank", "noopener");
  };
  const shareLI = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SITE_URL)}`, "_blank", "noopener");
  };
  const shareIG = () => {
    navigator.clipboard.writeText(fullText).then(() => {
      setIgCopied(true);
      setTimeout(() => setIgCopied(false), 3000);
      window.open("https://www.instagram.com/", "_blank", "noopener");
    });
  };
  const copyText = () => {
    navigator.clipboard.writeText(fullText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const btnClass = "flex items-center gap-3 w-full px-4 py-3 text-sm text-[var(--color-text)] hover:bg-[var(--color-surface-alt)]/80 hover:text-[var(--color-text)] transition-colors";

  return (
    <div ref={ref} className="absolute bottom-full mb-2 right-0 w-64 bg-[#111] border border-[var(--color-border)] shadow-2xl shadow-black/60 z-50 animate-in fade-in slide-in-from-bottom-2 duration-200" data-testid="share-dropdown">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--color-border)]">
        <span className="text-xs font-bold text-[var(--color-text-muted)] uppercase tracking-wider">{t.share_results || "Share results"}</span>
        <button onClick={onClose} className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"><X className="w-3.5 h-3.5" /></button>
      </div>
      <button data-testid="share-x" onClick={shareX} className={btnClass}>
        <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
        X (Twitter)
      </button>
      <button data-testid="share-facebook" onClick={shareFB} className={btnClass}>
        <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
        Facebook
      </button>
      <button data-testid="share-linkedin" onClick={shareLI} className={btnClass}>
        <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
        LinkedIn
      </button>
      <button data-testid="share-instagram" onClick={shareIG} className={btnClass}>
        <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
        {igCopied ? (t.share_ig_copied || "Text copied! Paste in Instagram") : "Instagram"}
      </button>
      <div className="border-t border-[var(--color-border)]">
        <button data-testid="share-copy" onClick={copyText} className={btnClass}>
          {copied ? <Check className="w-4 h-4 shrink-0 text-emerald-400" /> : <Copy className="w-4 h-4 shrink-0" />}
          {copied ? (t.share_copied || "Copied!") : (t.share_copy || "Copy text")}
        </button>
      </div>
    </div>
  );
}

export function RevenueCalculator() {
  const { t } = useLanguage();
  const [industry, setIndustry] = useState("");
  const [calculated, setCalculated] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const resultsRef = useRef(null);

  const handleCalculate = (industryId) => {
    setIndustry(industryId);
    setShowShare(false);
    const ind = INDUSTRIES.find(i => i.id === industryId);
    if (!ind) return;

    const withAI = ind.aiTraffic;
    const withoutAI = Math.round(ind.aiTraffic * 0.15);
    const lostVisits = withAI - withoutAI;
    const lostConversions = Math.round(lostVisits * (ind.convRate / 100));
    const lostRevenue = Math.round(lostConversions * ind.avgOrder);
    const yearlyLoss = lostRevenue * 12;
    const roi = Math.round(lostRevenue / 49);

    setCalculated({
      withAI, withoutAI, lostVisits, lostConversions, lostRevenue, yearlyLoss, roi,
      industry: ind,
    });

    // Subtle haptic confirmation on mobile so the user feels the tap
    // landed *before* the scroll animation finishes. Browsers without the
    // Vibration API (Safari iOS) silently no-op.
    try {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate(15);
      }
    } catch { /* ignore */ }
  };

  // Auto-scroll to results when they appear/change. Critical on mobile —
  // results live BELOW the industry-button grid, so without this the user
  // taps and visually nothing happens (results are off-screen).
  useEffect(() => {
    if (!calculated || !resultsRef.current) return;
    // Defer to next frame so the DOM has the rendered card before measuring.
    const raf = requestAnimationFrame(() => {
      try {
        const node = resultsRef.current;
        if (!node) return;
        const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
        node.scrollIntoView({
          behavior: "smooth",
          block: isMobile ? "start" : "center",
        });
      } catch {
        resultsRef.current?.scrollIntoView();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [calculated]);

  const shareText = calculated
    ? (t.share_text || "My business is losing €{amount}/month without AI visibility!").replace("{amount}", calculated.lostRevenue.toLocaleString())
    : "";

  return (
    <div data-testid="revenue-calculator">
      {/* Industry Selection */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-8">
        {INDUSTRIES.map(ind => (
          <button
            key={ind.id}
            data-testid={`industry-${ind.id}`}
            onClick={() => handleCalculate(ind.id)}
            className={`p-4 border text-left text-sm font-medium transition-all ${
              industry === ind.id
                ? 'bg-[#a855f7]/10 border-[#a855f7] text-white'
                : 'bg-[var(--color-bg)] border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-text)]'
            }`}
          >
            {t[ind.tKey] || ind.id}
          </button>
        ))}
      </div>

      {/* Results */}
      {calculated && (
        <div ref={resultsRef} className="space-y-6 animate-in fade-in scroll-mt-24" data-testid="calc-results">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-[var(--color-bg)] border border-emerald-800/50 p-6">
              <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase tracking-wider mb-2">{t.calc_with_ai || "With AI Visibility"}</p>
              <p className="text-2xl sm:text-4xl font-black font-['JetBrains_Mono'] text-emerald-400">{calculated.withAI.toLocaleString()}</p>
              <p className="text-[var(--color-text-muted)] text-xs mt-1">{t.calc_visits_ai || "visits/month from AI search"}</p>
            </div>
            <div className="bg-[var(--color-bg)] border border-[#a855f7]/30 p-6">
              <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase tracking-wider mb-2">{t.calc_without_ai || "Without AI Visibility"}</p>
              <p className="text-2xl sm:text-4xl font-black font-['JetBrains_Mono'] text-[#a855f7]">{calculated.withoutAI.toLocaleString()}</p>
              <p className="text-[var(--color-text-muted)] text-xs mt-1">{t.calc_less || "visits/month (85% less)"}</p>
            </div>
            <div className="bg-[var(--color-bg)] border border-[#FFE600]/30 p-6">
              <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase tracking-wider mb-2">{t.calc_lost_month || "Lost Revenue / Month"}</p>
              <p className="text-2xl sm:text-4xl font-black font-['JetBrains_Mono'] text-[#FFE600]">€{calculated.lostRevenue.toLocaleString()}</p>
              <p className="text-[var(--color-text-muted)] text-xs mt-1">{calculated.lostConversions} {t.calc_lost_conv || "lost conversions"}</p>
            </div>
            <div className="bg-[var(--color-bg)] border border-[#a855f7] p-6">
              <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase tracking-wider mb-2">{t.calc_yearly || "Yearly Loss"}</p>
              <p className="text-2xl sm:text-4xl font-black font-['JetBrains_Mono'] text-[#a855f7]">€{calculated.yearlyLoss.toLocaleString()}</p>
              <p className="text-[var(--color-text-muted)] text-xs mt-1">{t.calc_no_action || "per year without action"}</p>
            </div>
          </div>

          <div className="bg-[var(--color-bg)] border border-[var(--color-border)] p-4 sm:p-6 flex flex-col items-stretch gap-4 overflow-hidden">
            <div>
              <p className="text-[var(--color-text)] font-['JetBrains_Mono'] text-sm sm:text-base break-words">
                TopRankPulse {t.calc_costs || "costs"} <span className="text-[#FFE600] font-bold">€49/{t.calc_month || "month"}</span>. 
                {t.calc_losing || "You're losing"} <span className="text-[#a855f7] font-bold">€{calculated.lostRevenue.toLocaleString()}/{t.calc_month || "month"}</span>.
              </p>
              <p className="text-emerald-400 text-sm font-bold mt-1">
                ROI: {calculated.roi}x {t.calc_roi || "return on investment"}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="relative">
                <button
                  data-testid="share-results-btn"
                  onClick={() => setShowShare(v => !v)}
                  className="w-full sm:w-auto px-4 py-3 border border-zinc-600 text-[var(--color-text)] hover:border-[#FFE600] hover:text-[#FFE600] transition-all flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider"
                >
                  <Share2 className="w-4 h-4 shrink-0" />
                  {t.share_results || "Share results"}
                </button>
                {showShare && <ShareDropdown shareText={shareText} t={t} onClose={() => setShowShare(false)} />}
              </div>
              <a href="#pricing" className="px-6 py-3 bg-[#FFE600] text-black font-black uppercase tracking-wider hover:bg-yellow-300 transition-colors text-center text-sm sm:text-base" data-testid="calc-cta">
                {t.calc_stop || "STOP LOSING"} €{calculated.lostRevenue.toLocaleString()}/{t.calc_mo || "MO"} <ArrowRight className="w-4 h-4 inline ml-1" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
